import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output, ViewEncapsulation } from '@angular/core';
import { CommunicationType } from 'common/store/genesys-cloud/conversations/common/models/communication-type.enum';
import { GenesysCloudUser } from 'common/store/genesys-cloud/users/models/user.model';
import { Store } from '@ngrx/store';
import { filter, Subject, takeUntil } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { transferEmailConversationToUser, transferEmailConversationFailure, transferEmailConversationSuccess, transferEmailConversationToQueue } from 'common/store/genesys-cloud/conversations/emails/actions/email-conversation.actions';
import { transferMessageConversationToUser, transferMessageConversationFailure, transferMessageConversationSuccess, transferMessageConversationToQueue } from 'common/store/genesys-cloud/conversations/messages/actions/message-conversation.actions';
import { GenesysCloudQueue } from 'common/store/genesys-cloud/queues/models/genesys-cloud-queue.model';

@Component({
  selector: 'app-genesys-transfer-panel',
  templateUrl: './genesys-transfer-panel.component.html',
  styleUrls: ['./genesys-transfer-panel.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetectionStrategy: ChangeDetectionStrategy.OnPush
})
export class GenesysTransferPanelComponent implements OnDestroy{
  @Input()
  conversationId: string;
  @Input()
  agentParticipantId: string;
  @Input()
  communicationType: CommunicationType;

  @Output()
  cancel = new EventEmitter<void>();

  selectedUser: GenesysCloudUser;
  selectedQueue: GenesysCloudQueue;
  hasError: boolean;

  private readonly onDestroySubject = new Subject<void>();

  constructor(readonly changeDetectorRef: ChangeDetectorRef,
              private readonly store: Store,
              private readonly actions: Actions) {

    actions.pipe(
      ofType(transferMessageConversationSuccess, transferEmailConversationSuccess),
      filter(action => action.conversationId === this.conversationId),
      takeUntil(this.onDestroySubject)
    ).subscribe(() => this.onTransferSuccess());

    actions.pipe(
      ofType(transferMessageConversationFailure, transferEmailConversationFailure),
      filter(action => action.conversationId === this.conversationId),
      takeUntil(this.onDestroySubject)
    ).subscribe(() => this.onError());
  }

  ngOnDestroy(): void {
    this.onDestroySubject.next();
    this.onDestroySubject.complete();
  }

  userSelected(user: GenesysCloudUser) {
    this.selectedUser = user;
    this.selectedQueue = null;
    this.changeDetectorRef.detectChanges();
  }

  queueSelected(queue: GenesysCloudQueue) {
    this.selectedQueue = queue;
    this.selectedUser = null;
    this.changeDetectorRef.detectChanges();
  }

  onTransferSuccess() {
    this.cancel.emit();
  }

  onTransfer() {
    if (this.selectedUser) {
      this.hasError = false;
      this.transferConversationToUser();
    } else if (this.selectedQueue) {
      this.hasError = false;
      this.transferConversationToQueue();
    }
  }

  transferConversationToUser(): void {
    if (this.communicationType === CommunicationType.Message) {
      this.store.dispatch(transferMessageConversationToUser({ conversationId: this.conversationId, participantId: this.agentParticipantId, userId: this.selectedUser.id }));
    } else if (this.communicationType === CommunicationType.Email) {
      this.store.dispatch(transferEmailConversationToUser({ conversationId: this.conversationId, participantId: this.agentParticipantId, userId: this.selectedUser.id }));
    } else {
      throw new Error(`Unsupported communication type: ${ this.communicationType }`);
    }
  }

  transferConversationToQueue(): void {
    if (this.communicationType === CommunicationType.Message) {
      this.store.dispatch(transferMessageConversationToQueue({ conversationId: this.conversationId, participantId: this.agentParticipantId, queueId: this.selectedQueue.id }));
    } else if (this.communicationType === CommunicationType.Email) {
      this.store.dispatch(transferEmailConversationToQueue({ conversationId: this.conversationId, participantId: this.agentParticipantId, queueId: this.selectedQueue.id }));
    } else {
      throw new Error(`Unsupported communication type: ${ this.communicationType }`);
    }
  }

  private onError() {
    this.hasError = true;
    this.changeDetectorRef.detectChanges();
  }
}
